import { Box, Typography } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import { FormField, FormSelect } from "./FormControl";
import { BookingData } from "../../../../../types/bookings";

// ── Props ────────────────────────────────────────────────────────────────────

interface Props {
  bookingData: BookingData;
  setBookingData: React.Dispatch<React.SetStateAction<BookingData>>;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

// Section heading — mirrors the visual weight of DeceasedInformation's h5
function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="h6" fontWeight={600} mb={2} mt={1}>
      {children}
    </Typography>
  );
}

// ── Component ────────────────────────────────────────────────────────────────

export function ConferenceServiceInformation({
  bookingData,
  setBookingData,
}: Props) {
  const set = (
    field: keyof BookingData,
    value: BookingData[keyof BookingData],
  ) => setBookingData((prev) => ({ ...prev, [field]: value }));

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Typography variant="h5" fontWeight={600} mb={3}>
          Conference Details
        </Typography>

        {/* ── Basic Information ─────────────────────────────────────────── */}
        <SectionHeading>Basic Information</SectionHeading>

        {/* Conference Name + Type */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}
        >
          <FormField
            label="Conference Name"
            value={bookingData.title ?? ""}
            onChange={(v) => set("title", v)}
          />

          <FormSelect
            label="Conference Type"
            value={bookingData.conference_type ?? ""}
            onChange={(v) => set("conference_type", v)}
            options={[
              "summit",
              "workshop",
              "symposium",
              "dinner",
              "breakfast_meeting",
              "congress",
              "conference",
              "other",
            ]}
          />
        </Box>

        {/* Start / End dates */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr 1fr" },
            gap: 2,
            mt: 2,
          }}
        >
          <FormField
            label="Start Date"
            value={bookingData.start_date_of_event ?? ""}
            onChange={(v) => set("start_date_of_event", v)}
            type="date"
          />
          <FormField
            label="End Date"
            value={bookingData.end_date_of_event ?? ""}
            onChange={(v) => set("end_date_of_event", v)}
            type="date"
          />
          <FormField
            label="Start Time"
            value={bookingData.start_time ?? ""}
            onChange={(v) => set("start_time", v)}
            type="time"
          />
          <FormField
            label="End Time"
            value={bookingData.end_time ?? ""}
            onChange={(v) => set("end_time", v)}
            type="time"
          />
        </Box>

        {/* Description */}
        <Box mt={2}>
          <FormField
            label="Conference Description"
            value={bookingData.notes ?? ""}
            onChange={(v) => set("notes", v)}
            multiline
            rows={3}
            placeholder="Briefly describe the purpose and agenda of the conference…"
          />
        </Box>

        {/* Audience size + Access type */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
            mt: 2,
          }}
        >
          <FormField
            label="Expected Audience Size"
            value={bookingData.expected_audience_size ?? ""}
            onChange={(v) => set("expected_audience_size", v)}
            type="number"
          />
        </Box>
      </Box>
    </LocalizationProvider>
  );
}
